import type { StorefrontSettings } from "@workspace/api-client-react";
import type { StoreProduct, Variant } from "./store";
import { getSalePricing } from "./sale-pricing";

type DeliverySettings = Pick<
  StorefrontSettings,
  "shippingRatePerKgBDT" | "pieceCountWeightsKg" | "defaultDeliveryWeightKg" | "deliveryWithoutPieceCountMode"
>;

export function getProductPieceCount(product: Pick<StoreProduct, "pieceCount" | "body_html" | "description">): number | null {
  const savedCount = product.pieceCount;
  return typeof savedCount === "number" && Number.isInteger(savedCount) && savedCount > 0
    ? savedCount
    : null;
}

export function estimateDeliveryBDT(
  product: Pick<StoreProduct, "pieceCount" | "body_html" | "description">,
  settings: DeliverySettings,
): number {
  const pieceCount = getProductPieceCount(product);
  if (pieceCount === null && settings.deliveryWithoutPieceCountMode === "not-included") return 0;
  const weight = pieceCount === null
    ? settings.defaultDeliveryWeightKg
    : settings.pieceCountWeightsKg[String(pieceCount)];
  const rate = settings.shippingRatePerKgBDT;
  if (weight === null || weight === undefined || rate === null || rate === undefined) return 0;
  return Math.round(weight * rate);
}

export function getProductPricing(
  product: StoreProduct,
  variant: Variant,
  settings: StorefrontSettings,
) {
  const minimumDiscountPercent = product.minimumDiscountPercent ?? settings.minSaleDiscountPercent;
  const base = getSalePricing(variant, settings.pricing, minimumDiscountPercent);
  const deliveryEstimateBDT = estimateDeliveryBDT(product, settings);
  return {
    ...base,
    salePriceBDT: base.salePriceBDT + deliveryEstimateBDT,
    originalPriceBDT: base.originalPriceBDT === null
    ? null
      : base.originalPriceBDT + deliveryEstimateBDT,
    deliveryEstimateBDT,
  };
}

export function getProductPriceSummary(product: StoreProduct, settings: StorefrontSettings) {
  const availableVariants = product.variants.filter(variant => variant.available);
  const variants = availableVariants.length ? availableVariants : product.variants;
  const prices = variants.map(variant => ({
    variant,
    pricing: getProductPricing(product, variant, settings),
  }));
  if (!prices.length) return null;

  const lowest = prices.reduce((result, candidate) =>
    candidate.pricing.salePriceBDT < result.pricing.salePriceBDT ? candidate : result,
  );
  return {
    lowestVariant: lowest.variant,
    lowestPricing: lowest.pricing,
    pricesDiffer: new Set(prices.map(candidate => candidate.pricing.salePriceBDT)).size > 1,
    isOnSale: lowest.variant.available && lowest.pricing.isOnSale,
    discountPercent: lowest.variant.available && lowest.pricing.isOnSale
      ? lowest.pricing.discountPercent
      : 0,
  };
}